import { Label } from '@fluentui/react';
import * as React from 'react';
import { Field, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { getLocaleText } from 'src/common/utilities/Utilities';
import Text from 'src/components/text/Text';
import localeService from 'src/dataServices/LocaleService';
import { IApplicationState } from 'src/state';
import { IFeatureFlagsState } from 'src/state/feature-flags';
import { IMeoLocale, MeoLocaleState } from 'src/state/meo-localization/types';
import { IProfile } from 'src/state/profile/types';
import { parseTextWithUnderline } from '../utilities/Helpers/parseTextWithUnderline';
import FieldValidationError from './FieldValidationError';
import './PrimaryContactEdit.scss';
import './ProfileForm.scss';
import {
  composeRequiredFieldValidator,
  hasBannedFirstAndLastNameInputValidation,
  startsOrEndsWithSpaceValidation,
  emailAddressValidator,
  emailRequiredFieldValidator,
  isNumericValidation,
  hasEmojisValidation,
  onlySpecialCharactersValidation,
  hasBannedMiddleNameInputValidation,
  onlyNonprintableCharactersValidation
} from './ProfileV2SharedComponents';
import RenderEmails from './RenderEmails';
import TextInputV2 from './TextInputV2';

interface IPropsFromState {
  featureFlags: IFeatureFlagsState;
  meoLocale: MeoLocaleState;
}

interface IPropsFromParent {
  profile: IProfile;
  WrappedFieldArrayProps: any;
  isCommercial: boolean;
  initialEmailLocale: IMeoLocale;
}

type PrimaryContactEditProps = IPropsFromState & IPropsFromParent;
type StringMap = Record<string, string>;

const getJobRoles = (map: StringMap) => {
  return Object.keys(map)
    .map((key) => ({ key, text: map[key] }))
    .sort((a, b) => a.text.localeCompare(b.text));
};

const validateJobRole = (value: string) => (value && value.trim() ? undefined : 'primaryContact.jobTitleRequiredError');

export const PrimaryContactEdit: React.FunctionComponent<PrimaryContactEditProps> = (props) => {
  const firstNameGenericValidationMessage = getLocaleText('ProfileV2', 'primaryContact.firstNameError');
  const middleNameGenericValidationMessage = getLocaleText('ProfileV2', 'primaryContact.middleNameError');
  const lastNameGenericValidationMessage = getLocaleText('ProfileV2', 'primaryContact.lastNameError');
  const tradescreeningPhaseTwoEnabled = props.featureFlags.tradescreeningPhaseTwoEnabled;
  const profilePageEnhancementsEnabled = props.featureFlags.profilePageEnhancementsEnabled;

  const firstNameValidators = tradescreeningPhaseTwoEnabled
    ? [
        composeRequiredFieldValidator('primaryContact.firstName'),
        hasBannedFirstAndLastNameInputValidation(firstNameGenericValidationMessage),
        startsOrEndsWithSpaceValidation(firstNameGenericValidationMessage),
        onlyNonprintableCharactersValidation(firstNameGenericValidationMessage),
        onlySpecialCharactersValidation(firstNameGenericValidationMessage),
        isNumericValidation(firstNameGenericValidationMessage),
        hasEmojisValidation(firstNameGenericValidationMessage)
      ]
    : [composeRequiredFieldValidator('primaryContact.firstName')];
  const middleNameValidators = tradescreeningPhaseTwoEnabled
    ? [
        startsOrEndsWithSpaceValidation(middleNameGenericValidationMessage),
        onlyNonprintableCharactersValidation(middleNameGenericValidationMessage),
        onlySpecialCharactersValidation(middleNameGenericValidationMessage),
        hasEmojisValidation(middleNameGenericValidationMessage),
        hasBannedMiddleNameInputValidation(middleNameGenericValidationMessage)
      ]
    : [];
  const lastNameValidators = tradescreeningPhaseTwoEnabled
    ? [
        composeRequiredFieldValidator('primaryContact.lastName'),
        hasBannedFirstAndLastNameInputValidation(lastNameGenericValidationMessage),
        startsOrEndsWithSpaceValidation(lastNameGenericValidationMessage),
        onlyNonprintableCharactersValidation(lastNameGenericValidationMessage),
        onlySpecialCharactersValidation(lastNameGenericValidationMessage),
        isNumericValidation(lastNameGenericValidationMessage),
        hasEmojisValidation(lastNameGenericValidationMessage)
      ]
    : [composeRequiredFieldValidator('primaryContact.lastName')];
  const jobRolesText = (localeService.getContent('ProfileV2', 'jobRoles') || {}) as StringMap;
  const jobRoleOptions = getJobRoles(jobRolesText);

  return (
    <div id="org-info-part3">
      <div id="profile-primary-contact-description">
        <p>{localeService.getText('ProfileV2', 'PrimaryContactDescription')}</p>
      </div>
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="primaryContact.firstName"
            localeCategoryId="ProfileV2"
            localeLabelId="FirstName"
            required={true}
            maxLength={40}
            validators={firstNameValidators}
            iconName={"info"}
            tooltipContent={ tradescreeningPhaseTwoEnabled
              ? <ul className="tooltip-list">
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameDisallowedValuesValidation')}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.nameSpecialCharactersValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.nameOnlySpacesValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.numbersOnlyValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.nameNonPrintableCharactersValidation'))}</li>
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameLeadingTrailingSpacesValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameLengthValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameEmojisValidation')}</li>
              </ul>
              : undefined
            }
          />
        </div>
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="primaryContact.middleName"
            localeCategoryId="ProfileV2"
            localeLabelId="MiddleName"
            required={false}
            maxLength={40}
            validators={middleNameValidators}
            iconName={"info"}
            tooltipContent={tradescreeningPhaseTwoEnabled
              ? <ul className="tooltip-list">
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.nameSpecialCharactersValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.nameOnlySpacesValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.nameNonPrintableCharactersValidation'))}</li>
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameLeadingTrailingSpacesValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameLengthValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameEmojisValidation')}</li>
              </ul>
              : undefined
            }
          />
        </div>
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="primaryContact.lastName"
            localeCategoryId="ProfileV2"
            localeLabelId="LastName"
            required={true}
            maxLength={40}
            validators={lastNameValidators}
            iconName={"info"}
            tooltipContent={tradescreeningPhaseTwoEnabled
              ? <ul className="tooltip-list">
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameDisallowedValuesValidation')}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.nameSpecialCharactersValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.nameOnlySpacesValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.numbersOnlyValidation'))}</li>
                <li>{parseTextWithUnderline(getLocaleText('ProfileV2', 'primaryContact.nameNonPrintableCharactersValidation'))}</li>
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameLeadingTrailingSpacesValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameLengthValidation')}</li>
                <li>{getLocaleText('ProfileV2', 'primaryContact.nameEmojisValidation')}</li>
              </ul>
              : undefined
            }
          />
        </div>
      </div>
      <div className="ms-Grid-row mt-2">
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <TextInputV2
            id="primaryContact.email"
            localeCategoryId="ProfileV2"
            localeLabelId="PrimaryEmailAddress"
            required={true}
            validators={[emailRequiredFieldValidator, emailAddressValidator]}
          />
        </div>
        <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
          <Label tabIndex={-1} htmlFor="emailLanguagePreference">
            <Text category="ProfileV2" id="EmailLanguagePreferenceLabel" />
          </Label>
          <Field
            name="primaryContact.emailLanguagePreference"
            id="primaryContact.emailLanguagePreference"
            component={RenderMeoLocalesDropdown}
            props={{
              meoLocales: props.meoLocale.locales
            }}
            initialValue={props.initialEmailLocale.cultureCode}
          />
        </div>
        {profilePageEnhancementsEnabled && (
          <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
            <Label tabIndex={-1} htmlFor="primaryContact.jobRole">
              {'* '}
              <Text category="ProfileV2" id="JobRole" />
            </Label>
            <Field
              name="primaryContact.jobRole"
              id="primaryContact.jobRole"
              validate={validateJobRole}
              component={RenderJobRolesDropdown}
              props={{
                jobRoleOptions
              }}
            />
          </div>
        )}
      </div>
      <FieldArray name="notificationEmails">{({ fields }) => <RenderEmails fields={fields} />}</FieldArray>
    </div>
  );
};

interface IRenderMeoLocalesDropdown {
  input: any;
  meta: { touched: boolean; error: string };
  props: {
    meoLocales: IMeoLocale[];
  };
}

interface IRenderJobRolesDropdown {
  input: any;
  meta: { touched: boolean; error: string; submitFailed: boolean };
  props: {
    jobRoleOptions: { key: string; text: string }[];
  };
}

const RenderMeoLocalesDropdown = ({
  input,
  meta: { touched, error },
  props: { meoLocales }
}: IRenderMeoLocalesDropdown) => {
  return (
    <div>
      <div className="custom-select">
        <select
          className="custom-select-input"
          onBlur={input.onBlur}
          onChange={(e) => {
            input.onChange(e);
          }}
          value={input.value}
          aria-label={localeService.getText('ProfileV2', `EmailLanguagePreferenceLabel`)}
        >
          {(meoLocales || [])
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
            .map((meoLocale, _index) => (
              <option key={meoLocale.cultureCode} value={meoLocale.cultureCode} lang={meoLocale.cultureCode}>
                {meoLocale.displayName}
              </option>
            ))}
        </select>
        <span className="custom-select-arrow" aria-hidden="true" />
      </div>
      {touched && error && <FieldValidationError localeErrorTextId={error} />}
    </div>
  );
};

const RenderJobRolesDropdown = ({
  input,
  meta: { touched, error, submitFailed },
  props: { jobRoleOptions }
}: IRenderJobRolesDropdown) => {
  return (
    <div>
      <div className="custom-select">
        <select
          className="custom-select-input"
          onBlur={input.onBlur}
          onChange={(e) => {
            input.onChange(e);
          }}
          value={input.value || ''}
          required
          aria-label={localeService.getText('ProfileV2', 'JobRole')}
        >
          <option value="">{localeService.getText('ProfileV2', 'JobRolePlaceholder')}</option>
          {jobRoleOptions.map((jobRoleOption) => (
            <option key={jobRoleOption.key} value={jobRoleOption.key}>
              {jobRoleOption.text}
            </option>
          ))}
        </select>
        <span className="custom-select-arrow" aria-hidden="true" />
      </div>
      {(touched || submitFailed ) && error && <FieldValidationError localeErrorTextId={error} />}
    </div>
  );
};

const mapStateToProps = ({ flags, meoLocale }: IApplicationState) => ({
  featureFlags: flags,
  meoLocale: meoLocale
});

export default connect(mapStateToProps)(PrimaryContactEdit);
